import React from 'react';
import ToolListItem from './ToolListItem';
import type { ToolKey, PremiumQuotas } from '../../api/premiumService';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

type ToolDef = {
  id: ToolKey;
  icon: React.ComponentType<any>;
  titleKey: string;
  descKey: string;
};

type Props = {
  tools: ToolDef[];
  active: ToolKey;
  quotas: PremiumQuotas | null;
  onSelect: (id: ToolKey) => void;
  maxHeight?: number; // px
};

const ToolsList: React.FC<Props> = ({ tools, active, quotas, onSelect, maxHeight = 200 }) => {
  const { t } = useTranslation();
  const q = (k: ToolKey) => (quotas ? quotas[k] : 0);

  const ref = React.useRef<HTMLDivElement>(null);
  const [atTop, setAtTop] = React.useState(true);
  const [atBottom, setAtBottom] = React.useState(false);

  const updateShadows = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    setAtTop(scrollTop <= 0);
    setAtBottom(scrollTop + clientHeight >= scrollHeight - 1);
  }, []);

  React.useEffect(() => {
    updateShadows();
    const el = ref.current;
    if (!el) return;
    const onResize = () => updateShadows();
    el.addEventListener('scroll', updateShadows);
    window.addEventListener('resize', onResize);
    return () => {
      el.removeEventListener('scroll', updateShadows);
      window.removeEventListener('resize', onResize);
    };
  }, [updateShadows]);

  return (
    <section
      className="relative rounded-2xl border border-slate-200 shadow-sm p-3
                 bg-gradient-to-b from-sky-50 to-indigo-50"
      aria-labelledby="toolslist-title"
    >
      {/* Título */}
      <div className="bg-white px-4 py-3 rounded-t-2xl">
        <h3 id="toolslist-title" className="text-lg font-bold text-center text-indigo-600">
          {t('tools.passive_title')}
        </h3>
      </div>

      <div className="h-px bg-slate-300/60" />

      {/* Área scrollable */}
      <div
        ref={ref}
        className="relative z-0 overflow-y-auto pr-1 overscroll-contain scroll-smooth"
        style={{ maxHeight: `calc(100vh - ${maxHeight}px)` }}
        role="list"
        aria-label={t('tools.passive_title') as string}
      >
        {/* ⬇️ El contenido por encima de los gradientes */}
        <div className="relative z-10 space-y-3">
          {tools.map(({ id, icon, titleKey, descKey }) => (
            <ToolListItem
              key={id}
              id={id}
              icon={icon}
              titleKey={titleKey}
              descKey={descKey}
              selected={active === id}
              quota={q(id)}
              onClick={() => onSelect(id)}
            />
          ))}
        </div>

        {/* Gradiente superior (detrás del contenido) */}
        {!atTop && (
          <div
            aria-hidden
            className="pointer-events-none absolute z-0 top-0 left-0 right-0 h-6
                       bg-gradient-to-b from-sky-50 to-transparent"
          />
        )}

        {/* Gradiente inferior (detrás del contenido) */}
        {!atBottom && (
          <div
            aria-hidden
            className="pointer-events-none absolute z-0 bottom-0 left-0 right-0 h-10
                       bg-gradient-to-t from-indigo-50 to-transparent
                       flex items-end justify-center"
          >
            <ChevronDown className="mb-1 h-4 w-4 text-slate-300" />
          </div>
        )}
      </div>
    </section>
  );
};

export default ToolsList;
